import SearchCriteriaService, {
	type SearchCriteria,
	type SearchCriteriaResults,
} from '@/lib/SearchCriteriaService';
import { getValidAccessToken } from '@/lib/supabase/getValidAccessToken';
import { atom } from 'nanostores';

export const $searchcriteria = atom< SearchCriteria[] >( [] );
export const $searchcriteriaLoading = atom< boolean >( false );
export const $searchcriteriaError = atom< string | null >( null );

// Store for search criteria results (count + first estate)
export const $searchcriteriaResults = atom<
	Record< string, SearchCriteriaResults >
>( {} );
export const $searchcriteriaResultsLoading = atom< Record< string, boolean > >(
	{}
);

// Local storage key for pending search criteria
const LOCAL_STORAGE_KEY = 'pending_search_criteria';

// Local storage functions for pending search criteria
const getPendingSearchCriteria = (): SearchCriteria[] => {
	try {
		const pending = localStorage.getItem( LOCAL_STORAGE_KEY );
		return pending ? JSON.parse( pending ) : [];
	} catch ( error ) {
		// eslint-disable-next-line no-console
		console.error(
			'Error reading pending search criteria from localStorage:',
			error
		);
		return [];
	}
};

const savePendingSearchCriteria = ( searchCriteria: SearchCriteria[] ) => {
	try {
		localStorage.setItem(
			LOCAL_STORAGE_KEY,
			JSON.stringify( searchCriteria )
		);
	} catch ( error ) {
		// eslint-disable-next-line no-console
		console.error(
			'Error saving pending search criteria to localStorage:',
			error
		);
	}
};

const addPendingSearchCriteria = ( searchCriteria: SearchCriteria ) => {
	const pending = getPendingSearchCriteria();
	pending.push( searchCriteria );
	savePendingSearchCriteria( pending );
};

// Atomically claim all pending search criteria: read and clear localStorage
// in one synchronous step so concurrent sync invocations (duplicate auth
// events, other browser tabs) cannot pick up the same items.
const takePendingSearchCriteria = (): SearchCriteria[] => {
	const pending = getPendingSearchCriteria();
	if ( pending.length > 0 ) {
		clearPendingSearchCriteria();
	}
	return pending;
};

const restorePendingSearchCriteria = ( items: SearchCriteria[] ) => {
	if ( items.length === 0 ) return;
	savePendingSearchCriteria( [ ...getPendingSearchCriteria(), ...items ] );
};

const clearPendingSearchCriteria = () => {
	try {
		localStorage.removeItem( LOCAL_STORAGE_KEY );
	} catch ( error ) {
		// eslint-disable-next-line no-console
		console.error(
			'Error clearing pending search criteria from localStorage:',
			error
		);
	}
};

// Helper functions for local store management
const addSearchcriteria = ( searchcriteria: SearchCriteria ) => {
	const current = $searchcriteria.get();
	// Idempotent: a concurrent loadSearchcriteria() may already have fetched
	// the just-created entry from the backend — don't append it twice.
	if (
		searchcriteria.id &&
		current.some(
			( sc ) => String( sc.id ) === String( searchcriteria.id )
		)
	) {
		return;
	}
	// Prepend: the backend lists profiles by editdate DESC (newest first),
	// so a just-created profile belongs at the top.
	$searchcriteria.set( [ searchcriteria, ...current ] );
};

const removeSearchcriteria = ( id: string ) => {
	$searchcriteria.set(
		$searchcriteria.get().filter( ( sc ) => sc.id !== id )
	);
};

const updateSearchcriteria = (
	id: string,
	updatedSearchCriteria: SearchCriteria
) => {
	$searchcriteria.set(
		$searchcriteria
			.get()
			.map( ( sc ) => ( sc.id === id ? updatedSearchCriteria : sc ) )
	);
};

export const loadSearchcriteria = async () => {
	try {
		$searchcriteriaLoading.set( true );
		$searchcriteriaError.set( null );

		// Get a valid access token (refreshes an expired session first)
		const result = await getValidAccessToken();

		if ( result.status === 'error' ) {
			throw new Error( result.message );
		}
		if ( result.status !== 'ok' ) {
			throw new Error( 'Nicht authentifiziert' );
		}

		const searchCriteriaService = new SearchCriteriaService();
		const data = await searchCriteriaService.getSearchCriteria(
			result.token
		);
		$searchcriteria.set( data );
	} catch ( err ) {
		$searchcriteriaError.set(
			err instanceof Error
				? err.message
				: 'Ein unbekannter Fehler ist aufgetreten'
		);
	} finally {
		$searchcriteriaLoading.set( false );
	}
};

export const createSearchcriteria = async (
	filters: SearchCriteria[ 'filters' ],
	title: string = ''
): Promise< SearchCriteria > => {
	try {
		$searchcriteriaLoading.set( true );
		$searchcriteriaError.set( null );

		// Get a valid access token (refreshes an expired session first)
		const result = await getValidAccessToken();

		if ( result.status === 'error' ) {
			// Token retrieval failed — the member may still be logged in, so
			// don't silently divert to the anonymous pending path.
			throw new Error( result.message );
		}

		if ( result.status === 'no-session' ) {
			// User not authenticated, save to local storage
			const formattedDate = new Date().toLocaleDateString( 'de-DE', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			} );
			const pendingSearchCriteria: SearchCriteria = {
				id: '',
				title: title || `Suchprofil (${ formattedDate })`,
				created_at: new Date().toISOString(),
				filters,
				labels: {
					offerType: filters.offerType === 'kauf' ? 'Kauf' : 'Miete',
					estateType: filters.estateType,
				},
			};

			addPendingSearchCriteria( pendingSearchCriteria );

			// Show success message for local save
			$searchcriteriaError.set( null );

			return pendingSearchCriteria;
		}

		const searchCriteriaService = new SearchCriteriaService();
		const createdSearchCriteria =
			( await searchCriteriaService.createSearchCriteria(
				filters,
				title,
				result.token
			) ) as SearchCriteria;

		// Add to local store
		addSearchcriteria( createdSearchCriteria );

		return createdSearchCriteria;
	} catch ( err ) {
		$searchcriteriaError.set(
			err instanceof Error
				? err.message
				: 'Ein unbekannter Fehler ist aufgetreten'
		);
		throw err;
	} finally {
		$searchcriteriaLoading.set( false );
	}
};

export const deleteSearchcriteria = async ( id: string ) => {
	try {
		$searchcriteriaLoading.set( true );
		$searchcriteriaError.set( null );

		// Get a valid access token (refreshes an expired session first)
		const result = await getValidAccessToken();

		if ( result.status === 'error' ) {
			throw new Error( result.message );
		}
		if ( result.status !== 'ok' ) {
			throw new Error( 'Nicht authentifiziert' );
		}

		const searchCriteriaService = new SearchCriteriaService();
		await searchCriteriaService.deleteSearchCriteria( id, result.token );

		// Remove from local store
		removeSearchcriteria( id );
	} catch ( err ) {
		$searchcriteriaError.set(
			err instanceof Error
				? err.message
				: 'Ein unbekannter Fehler ist aufgetreten'
		);
	} finally {
		$searchcriteriaLoading.set( false );
	}
};

export const updateSearchcriteriaById = async (
	id: string,
	filters: SearchCriteria[ 'filters' ],
	title: string = ''
): Promise< SearchCriteria > => {
	try {
		$searchcriteriaLoading.set( true );
		$searchcriteriaError.set( null );

		// Get a valid access token (refreshes an expired session first)
		const result = await getValidAccessToken();

		if ( result.status === 'error' ) {
			throw new Error( result.message );
		}
		if ( result.status !== 'ok' ) {
			throw new Error( 'Nicht authentifiziert' );
		}

		const searchCriteriaService = new SearchCriteriaService();
		const updatedSearchCriteria =
			( await searchCriteriaService.updateSearchCriteria(
				id,
				filters,
				title,
				result.token
			) ) as SearchCriteria;

		// Update in local store
		updateSearchcriteria( id, updatedSearchCriteria );

		return updatedSearchCriteria;
	} catch ( err ) {
		$searchcriteriaError.set(
			err instanceof Error
				? err.message
				: 'Ein unbekannter Fehler ist aufgetreten'
		);
		throw err;
	} finally {
		$searchcriteriaLoading.set( false );
	}
};

// Function to sync pending search criteria to backend when user logs in.
// Idempotent: pending items are claimed atomically and concurrent calls are
// coalesced, so duplicate SIGNED_IN events (e.g. broadcast to other tabs)
// cannot create the same search profile twice.
let syncInFlight: Promise< void > | null = null;

export const syncPendingSearchCriteria = async (): Promise< void > => {
	if ( syncInFlight ) return syncInFlight;

	syncInFlight = ( async () => {
		// Claim pending items before any await so a concurrent invocation
		// finds an empty list.
		const pending = takePendingSearchCriteria();
		if ( pending.length === 0 ) {
			return;
		}

		try {
			const result = await getValidAccessToken();
			if ( result.status !== 'ok' ) {
				// Not authenticated (or retrieval failed): keep the items
				// pending for the next login.
				restorePendingSearchCriteria( pending );
				return;
			}

			$searchcriteriaLoading.set( true );

			const searchCriteriaService = new SearchCriteriaService();
			const failed: SearchCriteria[] = [];

			for ( const pendingCriteria of pending ) {
				try {
					const syncedCriteria =
						await searchCriteriaService.createSearchCriteria(
							pendingCriteria.filters,
							pendingCriteria.title,
							result.token
						);

					// Add to local store
					addSearchcriteria( syncedCriteria as SearchCriteria );
				} catch ( error ) {
					// eslint-disable-next-line no-console
					console.error(
						'Error syncing pending search criteria:',
						error
					);
					failed.push( pendingCriteria );
				}
			}

			// Keep failed items pending instead of dropping them.
			restorePendingSearchCriteria( failed );
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.error( 'Error syncing pending search criteria:', error );
		} finally {
			$searchcriteriaLoading.set( false );
			syncInFlight = null;
		}
	} )();

	return syncInFlight;
};

export const loadSearchCriteriaResults = async (
	searchCriteria: SearchCriteria
) => {
	try {
		// Set loading state for this specific search criteria
		const currentLoading = $searchcriteriaResultsLoading.get();
		$searchcriteriaResultsLoading.set( {
			...currentLoading,
			[ searchCriteria.id ]: true,
		} );

		const searchCriteriaService = new SearchCriteriaService();
		const results =
			await searchCriteriaService.executeSearchCriteria( searchCriteria );

		// Update results store
		const currentResults = $searchcriteriaResults.get();
		$searchcriteriaResults.set( {
			...currentResults,
			[ searchCriteria.id ]: results,
		} );
	} catch ( err ) {
		// eslint-disable-next-line no-console
		console.error(
			`Error loading results for search criteria ${ searchCriteria.id }:`,
			err
		);
	} finally {
		// Clear loading state
		const currentLoading = $searchcriteriaResultsLoading.get();
		const { [ searchCriteria.id ]: _, ...rest } = currentLoading;
		$searchcriteriaResultsLoading.set( rest );
	}
};

export const loadAllSearchCriteriaResults = async () => {
	const searchCriterias = $searchcriteria.get();

	// Load results for all search criteria in parallel
	await Promise.all(
		searchCriterias.map( ( searchCriteria ) =>
			loadSearchCriteriaResults( searchCriteria )
		)
	);
};
